<template>
  <div class="my-edits-page">
    <div class="page-container">
      <header class="page-header">
        <div>
          <h1 class="page-title">My Edits</h1>
          <p class="page-subtitle">Your developmental edits</p>
        </div>
        <a :href="uploadUrl" class="new-edit-button">New Edit</a>
      </header>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading your edits...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <svg class="error-icon" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd"
          />
        </svg>
        <p class="error-message">{{ error }}</p>
        <button @click="loadEdits" class="retry-button">Try Again</button>
      </div>

      <!-- Empty State -->
      <div v-else-if="edits.length === 0" class="empty-state">
        <svg class="empty-icon" viewBox="0 0 20 20" fill="currentColor">
          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
          <path
            fill-rule="evenodd"
            d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
            clip-rule="evenodd"
          />
        </svg>
        <h2>No edits yet</h2>
        <p>Upload a manuscript to get started with your first developmental edit.</p>
        <button @click="gotoUpload()" class="cta-button">Upload Manuscript</button>
      </div>

      <!-- Edits List -->
      <div v-else class="edits-grid">
        <div v-for="edit in edits" :key="edit.id" class="edit-card" @click="navigateToEdit(edit)">
          <div class="edit-header">
            <div class="edit-title">{{ edit.title || 'Untitled Manuscript' }}</div>
            <div class="edit-status" :class="`status-${edit.status}`">
              {{ getStatusLabel(edit.status) }}
            </div>
            <div class="edit-menu" @click.stop>
              <button class="kebab-btn" aria-label="Options" @click="toggleMenu(edit.id)">
                <QIcon :path="mdiDotsVertical" :size="18" color="currentColor" />
              </button>
              <div v-if="openMenuId === edit.id" class="menu-dropdown">
                <button class="menu-item danger" @click="handleDelete(edit)">
                  <QIcon :path="mdiTrashCanOutline" :size="16" color="currentColor" />
                  Delete
                </button>
              </div>
            </div>
          </div>

          <div class="edit-meta-grid">
            <span v-if="edit.author" class="meta-item">
              <svg class="meta-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
              </svg>
              {{ edit.author }}
            </span>
            <span class="meta-item meta-item-end">
              <svg class="meta-icon" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                  clip-rule="evenodd"
                />
              </svg>
              {{ formatWordCount(edit.wordCount) }}
            </span>

            <span class="meta-item">
              <QIcon :path="edit.isPublic ? mdiEarth : mdiLock" :size="16" class="meta-icon" color="currentColor" />
              {{ edit.isPublic ? 'Shared' : 'Private' }}
            </span>
            <span class="meta-item meta-item-end">
              <svg class="meta-icon" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                  clip-rule="evenodd"
                />
              </svg>
              {{ formatDate(edit.createdAt) }}
            </span>
          </div>

          <div v-if="edit.frameworks && edit.frameworks.length > 0" class="edit-frameworks">
            <span v-for="framework in edit.frameworks" :key="framework" class="framework-tag">
              {{ getFrameworkName(framework) }}
            </span>
          </div>

          <div v-if="edit.status === 'complete'" class="edit-actions">
            <span class="action-link">View Edit →</span>
          </div>
        </div>
      </div>

      <!--
        Partner program, shown only to customers who have actually paid.
        Gated on a credit purchase with amountPaid > 0, which excludes admin
        grants (those record amountPaid = 0). Someone on a comped credit has
        not experienced the paid product and should not be recruited to sell it.
      -->
      <a
        v-if="isPaidMember"
        class="partner-banner"
        href="https://edits.twig.io/partners/join"
        target="_blank"
        rel="noopener"
      >
        <span class="partner-banner-text">
          <strong>Know other writers?</strong>
          Share Twig. Earn 20% on every edit they buy.
        </span>
        <span class="partner-banner-cta">Become a partner →</span>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiDotsVertical, mdiEarth, mdiLock, mdiTrashCanOutline } from '@mdi/js';
import { onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import QIcon from '../components/QIcon.vue';
import { deleteProject, fetchWithAuth } from '../services/api';
import { useAuthStore } from '../stores/auth';

interface Edit {
  id: string;
  title: string;
  author?: string;
  wordCount: number;
  status: string;
  frameworks: string[];
  createdAt: string;
  isPublic?: boolean;
}

const router = useRouter();
const authStore = useAuthStore();

const edits = ref<Edit[]>([]);
const loading = ref(true);
const error = ref('');

/**
 * True only for customers who have actually paid. Admin-granted credits record
 * amountPaid = 0, so they are excluded deliberately.
 *
 * Defaults to false and stays false if the lookup fails: not showing the
 * banner to a paying customer is a missed impression, showing it to a free
 * user is a wrong promise.
 */
const isPaidMember = ref(false);

async function loadPaidStatus() {
  const uid = authStore.uid;
  if (!uid) return;
  try {
    const res = await fetchWithAuth(`/api/users/${uid}/credits`);
    if (!res.ok) return;
    const data = (await res.json()) as { purchases?: Array<{ amountPaid?: number }> };
    isPaidMember.value = (data.purchases ?? []).some(p => (p.amountPaid ?? 0) > 0);
  } catch {
    // Non-fatal: the edits list must render regardless.
  }
}
const openMenuId = ref<string | null>(null);

function toggleMenu(id: string) {
  openMenuId.value = openMenuId.value === id ? null : id;
}

function closeMenus() {
  openMenuId.value = null;
}

async function handleDelete(edit: Edit) {
  openMenuId.value = null;
  const name = edit.title || 'Untitled Manuscript';
  if (!window.confirm(`Delete "${name}"? You'll lose access. Support can restore it for 30 days.`)) {
    return;
  }
  try {
    await deleteProject(edit.id);
    edits.value = edits.value.filter(e => e.id !== edit.id);
  } catch (err) {
    console.error('Failed to delete edit:', err);
    window.alert('Something broke deleting that edit. Try again in a sec.');
  }
}

async function loadEdits() {
  loading.value = true;
  error.value = '';

  try {
    const response = await fetchWithAuth(`/api/users/${authStore.uid}/edits`);
    if (!response.ok) {
      throw new Error('Failed to load edits');
    }
    const data = await response.json();
    edits.value = data.edits;
  } catch (err) {
    error.value = 'Failed to load your edits. Please try again.';
    console.error('Failed to load edits:', err);
  } finally {
    loading.value = false;
  }
}

const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const uploadUrl = isDev ? '/' : 'https://twig.io/dev-edit';

function gotoUpload() {
  location.href = uploadUrl;
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: 'Pending',
    processing: 'Processing',
    complete: 'Complete',
    error: 'Error',
  };
  return labels[status] || status;
}

function formatWordCount(count: number): string {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k words`;
  }
  return `${count} words`;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}

const MODEL_NAME_TO_ID: Record<string, string> = {
  'Claude 3.7 Sonnet': 'anthropic/claude-3.7-sonnet',
  'Claude Sonnet 4.5': 'anthropic/claude-sonnet-4.5',
  'Gemini 2.5 Flash': 'google/gemini-2.5-flash',
  'Gemini 2.5 Pro': 'google/gemini-2.5-pro',
  'GPT-4.1': 'openai/gpt-4.1',
};

function navigateToEdit(edit: Edit) {
  if (edit.frameworks?.includes('benchmark')) {
    // Try localStorage context first (saved by benchmark store on creation)
    try {
      const ctx = JSON.parse(localStorage.getItem(`benchmark_context_${edit.id}`) ?? '');
      router.push(
        `/benchmark?projectIds=${ctx.projectIds.join(',')}&models=${encodeURIComponent(ctx.models.join(','))}&framework=${encodeURIComponent(ctx.framework)}&title=${encodeURIComponent(ctx.title)}`
      );
      return;
    } catch {
      /* no context saved yet */
    }

    // Fallback: find sibling model projects by matching title pattern
    const baseTitle = edit.title;
    const pattern = new RegExp(`^${baseTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} \\[Benchmark: (.+?)\\]$`);
    const siblings = edits.value
      .filter(e => pattern.test(e.title))
      .map(e => ({
        projectId: e.id,
        modelId: MODEL_NAME_TO_ID[e.title.match(pattern)?.[1] ?? ''] ?? '',
        frameworks: e.frameworks,
      }))
      .filter(s => s.modelId);

    if (siblings.length > 0) {
      const framework = siblings[0]!.frameworks.find(f => f !== 'benchmark') ?? '';
      router.push(
        `/benchmark?projectIds=${siblings.map(s => s.projectId).join(',')}&models=${encodeURIComponent(siblings.map(s => s.modelId).join(','))}&framework=${encodeURIComponent(framework)}&title=${encodeURIComponent(baseTitle)}`
      );
      return;
    }
  }
  router.push(`/edit/${edit.id}`);
}

function getFrameworkName(frameworkId: string): string {
  const names: Record<string, string> = {
    novel: 'Novel Analysis',
    'story-grid': 'Story Grid',
    'short-story': 'Short Story',
    roast: 'Roast',
    'query-letter': 'Query Package',
  };
  return names[frameworkId] || frameworkId;
}

onMounted(() => {
  loadEdits();
  loadPaidStatus();
  document.addEventListener('click', closeMenus);
});

onUnmounted(() => {
  document.removeEventListener('click', closeMenus);
});
</script>

<style scoped>
.my-edits-page {
  flex: 1;
  background: #f7fafc;
}

.page-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 3rem;
}

.new-edit-button {
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  background: var(--color-accent);
  border-radius: 6px;
  text-decoration: none;
  transition: all 0.2s;
  white-space: nowrap;
}

.new-edit-button:hover {
  background: var(--color-accent-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(97, 174, 212, 0.3);
}

.page-title {
  font-size: 2.25rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1.125rem;
  color: #718096;
}

/* Loading, Error, Empty States */
.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1.5rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p,
.empty-state p {
  color: #718096;
  font-size: 1rem;
  margin-top: 1rem;
}

.error-icon,
.empty-icon {
  width: 64px;
  height: 64px;
  color: #cbd5e0;
  margin: 0 auto 1.5rem;
}

.error-icon {
  color: #fc8181;
}

.error-message {
  color: #e53e3e;
  font-size: 1rem;
  margin-bottom: 1.5rem;
}

.retry-button,
.cta-button {
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  background: var(--color-accent);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.retry-button:hover,
.cta-button:hover {
  background: var(--color-accent-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(97, 174, 212, 0.3);
}

.empty-state h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 0.75rem;
}

/* Edits Grid */
.edits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 1.5rem;
}

.edit-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.edit-card:hover {
  border-color: #cbd5e0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.edit-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.edit-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a202c;
  line-height: 1.4;
  flex: 1;
}

.edit-status {
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 12px;
  white-space: nowrap;
}

.edit-menu {
  position: relative;
  flex-shrink: 0;
}

.kebab-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  color: #a0aec0;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
}

.kebab-btn:hover {
  color: #4a5568;
  background: #edf2f7;
}

.menu-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 10;
  min-width: 140px;
  padding: 0.25rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a5568;
  text-align: left;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
}

.menu-item:hover {
  background: #f7fafc;
}

.menu-item.danger {
  color: #e53e3e;
}

.menu-item.danger:hover {
  background: #fff5f5;
}

.status-pending {
  color: #975a16;
  background: #fef5e7;
}

.status-processing {
  color: #2c5282;
  background: #bee3f8;
}

.status-complete {
  color: #22543d;
  background: #c6f6d5;
}

.status-error {
  color: #742a2a;
  background: #fed7d7;
}

.edit-meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1rem;
  margin-bottom: 1rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: #718096;
}

.meta-item-end {
  justify-self: end;
}

.meta-icon {
  width: 16px;
  height: 16px;
  color: #a0aec0;
}

.edit-frameworks {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.framework-tag {
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-accent);
  background: var(--color-accent-light);
  border-radius: 12px;
}

.edit-actions {
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.action-link {
  color: var(--color-accent);
  font-size: 0.875rem;
  font-weight: 600;
  transition: color 0.2s;
}

.edit-card:hover .action-link {
  color: var(--color-accent-hover);
}
</style>

<style scoped>
/* Partner program banner. Sits below the edits so it never competes with the
   thing the user came here to do. */
.partner-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 2rem;
  padding: 1rem 1.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fafafa;
  text-decoration: none;
  transition: border-color 0.15s ease;
}

.partner-banner:hover {
  border-color: #3892bf;
}

.partner-banner-text {
  color: #282828;
  font-size: 0.95rem;
}

.partner-banner-text strong {
  font-weight: 600;
}

.partner-banner-cta {
  color: #3892bf;
  font-weight: 500;
  font-size: 0.95rem;
  white-space: nowrap;
}
</style>
